import type { StepKind, WorkflowSpec } from "~/services/pinnedArtifacts";

// Mermaid node shapes, chosen so the three step kinds read apart at a glance:
// a rounded box is data pulled from a table, a stadium is code that ran, and a
// plain box is something reused as-is.
const SHAPES: Record<StepKind, [string, string]> = {
  sql: ["(", ")"],
  code: ["([", "])"],
  frozen_input: ["[", "]"],
};

// Mermaid treats these as syntax inside a node label, so a summary containing
// one would break the diagram rather than render oddly.
const escapeLabel = (text: string): string =>
  text.replace(/["#]/g, " ").replace(/\s+/g, " ").trim().slice(0, 60);

const nodeId = (stepId: string): string => `step_${stepId.replace(/[^A-Za-z0-9_]/g, "_")}`;

const tableId = (table: string): string => `tbl_${table.replace(/[^A-Za-z0-9_]/g, "_")}`;

/** Render a frozen workflow as a mermaid flowchart.
 *
 * Built from the recipe rather than fetched, because the recipe *is* what a
 * refresh will run — a diagram drawn from live lineage could disagree with it
 * once the source session moves on.
 *
 * Source tables appear as their own nodes so the chain visibly bottoms out at
 * the dataset, which is the whole claim the pin makes.
 */
export function workflowToMermaid(spec: WorkflowSpec): string {
  const lines = ["graph TD"];
  const tables = new Set<string>();

  for (const step of spec.steps) {
    const [open, close] = SHAPES[step.kind];
    const label = escapeLabel(step.summary || step.step_id);
    lines.push(`  ${nodeId(step.step_id)}${open}"${label}"${close}`);
    for (const table of step.source_tables) tables.add(table);
  }

  for (const table of tables) {
    lines.push(`  ${tableId(table)}[("${escapeLabel(table)}")]`);
  }

  for (const step of spec.steps) {
    for (const table of step.source_tables) {
      lines.push(`  ${tableId(table)} --> ${nodeId(step.step_id)}`);
    }
    for (const input of step.inputs) {
      lines.push(`  ${nodeId(input)} --> ${nodeId(step.step_id)}`);
    }
  }

  // The target is what the card shows, so it is marked rather than left to be
  // inferred from position — a wide graph does not read top-to-bottom.
  lines.push(`  style ${nodeId(spec.target_step_id)} stroke-width:3px`);
  return lines.join("\n");
}
